import { ReactElement, ReactNode } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { OverlayContainer, useSortableDefaults } from '@components/drag-and-drop/defaults';
import { IconProps } from '@components/icons';
import { IconDropdown } from '@components/icons/dropdown';
import { IconWorkspace } from '@components/icons/workspace';
import { Nav } from '@components/nav';
import {
  NavExpandableSectionProps,
  NavGroupType,
  NavItemLinkProps,
  NavLinkPlaceholderContainer,
  ShownOnOpen,
} from '@components/nav/nav';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip, TooltipProps } from '@components/tooltip';
import { SelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export type Link = {
  id: string;
  href: string;
  icon: ReactElement<IconProps>;
  label: string;
  iconRight?: ReactElement<IconProps>;
  tooltip?: Pick<TooltipProps, 'content' | 'placement'>;
};

const withSortable = <T extends object>(
  BaseComponent: React.ComponentType<T>,
  SortableComponent: React.ComponentType<T>
) => {
  return (props: T) => {
    const Component = props.hasOwnProperty('onDragEnd') ? SortableComponent : BaseComponent;
    return <Component {...props} />;
  };
};

export type NavGroupCollapseProps = {
  id: string;
  label: string;
  links: Link[];
  toggleGroup: (id: string) => void;
  expandedSelectionStore: SelectionStore;
  onDragEnd?: (newOrder: Array<string>) => void;
};

export const NavGroupCollapse = ({
  toggleGroup,
  label,
  id,
  expandedSelectionStore,
  ...rest
}: NavGroupCollapseProps) => {
  const isCollapsed = expandedSelectionStore.useStore((state) => state[id]);

  return (
    <ShownOnOpen>
      <GroupContainer onClick={() => toggleGroup(id)}>
        <Stack space={0} orientation="horizontal" align="center">
          <Text text={label} font="bold14" color="purple20" />
          <IconContainer className={!isCollapsed ? 'arrow down' : 'arrow right'}>
            <IconDropdown color="purple50" />
          </IconContainer>
        </Stack>
      </GroupContainer>
      <Collapse isCollapsed={isCollapsed} overflowType="visible">
        <Links id={id} {...rest} />
      </Collapse>
    </ShownOnOpen>
  );
};

type LinksProps = Pick<NavGroupCollapseProps, 'id' | 'links' | 'onDragEnd'> & {
  isSortable?: boolean;
};

const BaseLinks = ({ links, isSortable, ...rest }: LinksProps) => {
  const Link = isSortable ? SortableLink : Nav.Link;
  const filteredLinks = links.filter((link) => link.href && link.icon && link.label);

  return (
    <Nav.Group>
      {filteredLinks.map(({ icon, id, href, tooltip, ...linkRest }) => {
        const key = id ?? href;
        const link = <Link key={key} iconLeft={icon} href={href} {...rest} {...linkRest} id={id} />;
        if (tooltip) {
          return (
            <Tooltip key={key} {...tooltip}>
              <div>{link}</div>
            </Tooltip>
          );
        }
        return link;
      })}
    </Nav.Group>
  );
};

const SortableLink = (props: NavItemLinkProps & BaseItem) => {
  return <SortableItem component={Nav.Link} {...props} />;
};

const SortableLinks = ({ onDragEnd, ...rest }: LinksProps) => {
  return (
    <SortableItems
      onDragEnd={onDragEnd}
      items={rest.links}
      renderPlaceholder={(link) => {
        const { icon, ...linkRest } = link as Link;
        return (
          <NavLinkPlaceholderContainer>
            <Nav.Link iconLeft={icon} {...linkRest} />
          </NavLinkPlaceholderContainer>
        );
      }}
      renderBase={(items) => <BaseLinks onDragEnd={onDragEnd} {...rest} links={items} isSortable />}
    />
  );
};

const Links = withSortable(BaseLinks, SortableLinks);

type BaseItem = {
  id: string;
};

type SortableItemsProps<Item extends BaseItem> = {
  items: Array<Item>;
  renderPlaceholder: (item: Item) => ReactNode;
  onDragEnd?: (newOrder: Array<string>) => void;
  renderBase: (items: Item[]) => ReactNode;
};

const SortableItems = <T extends BaseItem>({
  items: propItems,
  onDragEnd,
  renderPlaceholder,
  renderBase,
}: SortableItemsProps<T>) => {
  const {
    items,
    itemIds,
    activeItem,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortableDefaults({
    items: propItems,
    onDragEnd: (_items, ids) => onDragEnd?.(ids),
  });

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        {renderBase(items)}
      </SortableContext>

      <PortalDragOverlay dropAnimation={null}>
        {activeItem && <OverlayContainer>{renderPlaceholder(activeItem)}</OverlayContainer>}
      </PortalDragOverlay>
    </DndContext>
  );
};

type NavGroupOfGroupsCollapseProps = {
  id: string;
  label: string;
  groups: Array<NavGroupType>;
  addSidebar?: any;
  toggleGroup: (id: string) => void;
  onDragEnd?: (newOrder: Array<string>) => void;
  expandedSelectionStore: SelectionStore;
};

export const NavGroupOfGroupsCollapse = ({
  label,
  id,
  toggleGroup,
  expandedSelectionStore,
  addSidebar,
  ...rest
}: NavGroupOfGroupsCollapseProps) => {
  const state = expandedSelectionStore.useStore();
  const getIsCollapsed = (id: string) => state[id];
  const isCollapsed = getIsCollapsed(id);
  return (
    <>
      <Nav.GroupItemHeader
        iconLeft={<IconWorkspace size={16} color="buttonIconPositive" />}
        label={label}
        id={id}
        toggleGroup={toggleGroup}
        isCollapsed={getIsCollapsed}
        addSidebar={addSidebar}
      />
      <Collapse isCollapsed={isCollapsed} overflowType="visible">
        <NavGroups id={id} toggleGroup={toggleGroup} isCollapsed={getIsCollapsed} {...rest} />
      </Collapse>
    </>
  );
};

type SortableItemProps = {
  id: string;
  component: React.ComponentType<any>;
};

const SortableItem = ({ id, component: Component, ...rest }: SortableItemProps) => {
  const { attributes, listeners, setDraggableNodeRef, setDroppableNodeRef, transform, isDragging } =
    useSortable({
      id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    ...(isDragging && {
      opacity: 0,
    }),
  };

  return (
    <div ref={setDroppableNodeRef}>
      <div style={style} ref={setDraggableNodeRef}>
        <Component {...rest} {...attributes} {...listeners} />
      </div>
    </div>
  );
};

type NavGroupsProps = Pick<
  NavGroupOfGroupsCollapseProps,
  'groups' | 'onDragEnd' | 'id' | 'toggleGroup'
> & { isCollapsed: (id: string) => boolean };

type BaseNavGroupsProps = NavGroupsProps & {
  isSortable?: boolean;
};

const BaseNavGroups = ({ groups, id, isSortable = false, ...rest }: BaseNavGroupsProps) => {
  const ExpandableSectionHeader = isSortable
    ? SortableExpandableSectionHeader
    : Nav.ExpandableSectionHeader;

  return (
    <>
      {groups.map(({ id: groupId, ...groupRest }) => (
        <ExpandableSectionHeader
          key={groupId}
          groupId={groupId}
          collapseId={id}
          data-testid={groupRest.label}
          {...groupRest}
          {...rest}
        />
      ))}
    </>
  );
};

const SortableExpandableSectionHeader = (props: NavExpandableSectionProps) => {
  return <SortableItem id={props.groupId} component={Nav.ExpandableSectionHeader} {...props} />;
};

const SortableNavGroups = ({ onDragEnd, ...rest }: NavGroupsProps) => {
  return (
    <SortableItems
      onDragEnd={onDragEnd}
      items={rest.groups}
      renderPlaceholder={(group) => {
        return (
          <Nav.SectionPlaceholder
            group={group}
            isCollapsed={rest.isCollapsed(`${rest.id}-${group.id}`)}
          />
        );
      }}
      renderBase={(items) => (
        <BaseNavGroups onDragEnd={onDragEnd} {...rest} groups={items} isSortable />
      )}
    />
  );
};

const NavGroups = withSortable(BaseNavGroups, SortableNavGroups);

const GroupContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
})({
  cursor: 'pointer',
  height: pxToRem(32),
  borderRadius: pxToRem(4),
  paddingLeft: pxToRem(12),
  '&:hover': {
    background: theme.color.whiteTrans5,
  },
});

const IconContainer = styled.span({
  display: 'flex',
  transition: transitionStandard('transform'),
  '&.right': {
    transform: 'rotate(-90deg)',
  },
});
